/**
 * Onboarding Context
 * Simple and reliable onboarding state management with action triggers
 */

import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

export type OnboardingAction = 
  | 'none'
  | 'create-event'
  | 'create-venue'
  | 'invite-collaborator'
  | 'explore-songs';

interface OnboardingContextType {
  isActive: boolean;
  currentStep: number;
  pendingAction: OnboardingAction;
  completeStep: () => void;
  skip: () => void;
  restart: () => void;
  clearAction: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

const STORAGE_KEY = 'luma-onboarding';

const STEP_ACTIONS: OnboardingAction[] = [
  'none',              // welcome
  'create-event',     // create-event
  'create-venue',     // add-venue
  'invite-collaborator', // invite-team
  'explore-songs',    // explore-features
];

const STEP_ROUTES = [
  '/events',   // welcome -> events
  '/venues',  // create-event -> venues
  '/collaborators', // add-venue -> collaborators
  '/songs',   // invite-team -> songs
  '/songs',   // explore-features -> stays on songs
];

interface StoredState {
  isActive: boolean;
  currentStep: number;
}

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [isActive, setIsActive] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [pendingAction, setPendingAction] = useState<OnboardingAction>('none');
  const [isLoaded, setIsLoaded] = useState(false);

  // Load state from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as StoredState;
        setIsActive(parsed.isActive);
        setCurrentStep(parsed.currentStep || 0);
      }
    } catch (e) {
      console.error('Failed to load onboarding state:', e);
    }
    setIsLoaded(true);
  }, []);

  // Save state to localStorage
  const saveState = useCallback((newState: StoredState) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
    } catch (e) {
      console.error('Failed to save onboarding state:', e);
    }
  }, []);

  const clearAction = useCallback(() => {
    setPendingAction('none');
  }, []);

  const completeStep = useCallback(() => {
    const nextStep = currentStep + 1;
    if (nextStep >= STEP_ACTIONS.length) {
      setIsActive(false);
      setPendingAction('none');
      saveState({ isActive: false, currentStep: 0 });
    } else {
      setCurrentStep(nextStep);
      // Set the action for the NEW step we're going to
      const newAction = STEP_ACTIONS[nextStep] || 'none';
      setPendingAction(newAction);
      saveState({ isActive: true, currentStep: nextStep });
    }
  }, [currentStep, saveState]);

  const skip = useCallback(() => {
    setIsActive(false);
    setPendingAction('none');
    saveState({ isActive: false, currentStep });
  }, [currentStep, saveState]);

  const restart = useCallback(() => {
    setIsActive(true);
    setCurrentStep(0);
    setPendingAction(STEP_ACTIONS[1]); // Start with create-event action
    saveState({ isActive: true, currentStep: 0 });
  }, [saveState]);

  // Set initial action when onboarding becomes active
  useEffect(() => {
    if (isLoaded && isActive && pendingAction === 'none') {
      // Set action for current step
      const action = STEP_ACTIONS[currentStep] || 'none';
      if (action !== 'none') {
        setPendingAction(action);
      }
    }
  }, [isLoaded, isActive, currentStep, pendingAction]);

  return (
    <OnboardingContext.Provider
      value={{
        isActive: isLoaded && isActive,
        currentStep,
        pendingAction,
        completeStep,
        skip,
        restart,
        clearAction,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
}

// Export step data for the modal
export const ONBOARDING_STEPS = [
  { id: 'welcome', route: STEP_ROUTES[0] },
  { id: 'create-event', route: STEP_ROUTES[1] },
  { id: 'add-venue', route: STEP_ROUTES[2] },
  { id: 'invite-team', route: STEP_ROUTES[3] },
  { id: 'explore-features', route: STEP_ROUTES[4] },
];